import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
    Users,
    Plus,
    Search,
    Eye,
    Edit,
    CheckCircle,
    AlertTriangle,
    Trash2,
    Loader2,
    Mail,
    UserCheck,
    UserX,
    Clock
} from 'lucide-react';
import { apiClient, User } from '../services/api';
import CreateUserModal from './Modals/CreateUserModal';
import InviteUserModal from './Modals/InviteUserModal';
import TemporaryPasswordModal from './Modals/TemporaryPasswordModal';
import EditUserModal from './Modals/EditUserModal';
import ConfirmDeleteModal from './Modals/ConfirmDeleteModal';
import ConfirmReactivateModal from './Modals/ConfirmReactivateModal';
import UserViewModal from './Modals/UserViewModal';

interface UserManagementProps {
    user: any;
}

const UserManagement: React.FC<UserManagementProps> = ({ user }) => {
    // State management
    const [users, setUsers] = useState<User[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [totalCount, setTotalCount] = useState(0);
    const [categoryCount, setCategoryCount] = useState({
        activeUsers: 0,
        pendingInvites: 0,
        expiredInvites: 0
    });

    // Search and filter state
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showReactivateModal, setShowReactivateModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isInviting, setIsInviting] = useState(false);
    const [createdUserData, setCreatedUserData] = useState<{
        firstName: string;
        lastName: string;
        email: string;
        temporaryPassword: string;
    } | null>(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [userToDelete, setUserToDelete] = useState<User | null>(null);
    const [isReactivating, setIsReactivating] = useState(false);
    const [userToReactivate, setUserToReactivate] = useState<User | null>(null);
    const [userToView, setUserToView] = useState<User | null>(null);

    // Load users on component mount and when filters change
    useEffect(() => {
        loadUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, roleFilter, statusFilter, currentPage]);

    const loadUsers = async () => {
        try {
            setIsLoading(true);
            setError(null);

            const response = await apiClient.getUsers({
                search: searchTerm,
                page: currentPage,
                limit: pageSize,
                role: roleFilter,
                isActive: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
            });

            setUsers(response.data);
            setTotalCount(response.pagination.total);
            setCategoryCount(response.counts);
        } catch (err: any) {
            setError(err.message || 'Failed to load users');
            console.error('Error loading users:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const handleRoleFilter = (value: string) => {
        setRoleFilter(value);
        setCurrentPage(1);
    };

    const handleStatusFilter = (value: string) => {
        setStatusFilter(value);
        setCurrentPage(1);
    };

    const getRoleBadgeClass = (role: string) => {
        switch (role) {
            case 'super_admin':
                return 'bg-purple-100 text-purple-800';
            case 'admin':
                return 'bg-blue-100 text-blue-800';
            case 'manager':
                return 'bg-green-100 text-green-800';
            case 'compliance':
                return 'bg-yellow-100 text-yellow-800';
            case 'user':
                return 'bg-gray-100 text-gray-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const formatRoleName = (role: string) => {
        const roleNames: { [key: string]: string } = {
            'super_admin': 'Super Admin',
            'admin': 'Admin',
            'manager': 'Manager',
            'compliance': 'Compliance',
            'user': 'User'
        };
        return roleNames[role] || role;
    };

    const formatDate = (dateString: string | Date | null) => {
        if (!dateString) return 'Never';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const isPendingInvitation = (user: User) => {
        if (!user.invitationToken || !user.invitationExpiry) return false;
        const now = new Date();
        const expiry = new Date(user.invitationExpiry);
        return expiry > now;
    };

    const isExpiredInvitation = (user: User) => {
        if (!user.invitationToken || !user.invitationExpiry) return false;
        const now = new Date();
        const expiry = new Date(user.invitationExpiry);
        return expiry <= now;
    };

    const getUserStatus = (user: User) => {
        // Check for pending invitation first (regardless of isActive status)
        if (isPendingInvitation(user)) {
            return {
                label: 'Invitation Pending',
                icon: Clock,
                color: 'text-yellow-800',
                bgColor: 'bg-yellow-100'
            };
        }

        // Check for expired invitation
        if (isExpiredInvitation(user)) {
            return {
                label: 'Invitation Expired',
                icon: AlertTriangle,
                color: 'text-orange-800',
                bgColor: 'bg-orange-100'
            };
        }

        // Check active status
        if (!user.isActive) {
            return {
                label: 'Inactive',
                icon: UserX,
                color: 'text-red-800',
                bgColor: 'bg-red-100'
            };
        }

        return {
            label: 'Active',
            icon: UserCheck,
            color: 'text-green-800',
            bgColor: 'bg-green-100'
        };
    };

    const handleCreateUser = async (userData: {
        firstName: string;
        lastName: string;
        email: string;
        role: string;
    }) => {
        try {
            setIsCreating(true);
            setError(null);

            const response = await apiClient.createUser(userData);

            console.log('Create user response:', response); // DEBUG LINE
            console.log('Has temp password?', !!response.temporaryPassword); // DEBUG LINE

            setShowCreateModal(false);

            // Show temporary password in modal
            if (response.temporaryPassword) {
                setCreatedUserData({
                    firstName: userData.firstName,
                    lastName: userData.lastName,
                    email: userData.email,
                    temporaryPassword: response.temporaryPassword,
                });
                setShowPasswordModal(true);
            }

            await loadUsers();
        } catch (err: any) {
            setError(err.message || 'Failed to create user');
            throw err;
        } finally {
            setIsCreating(false);
        }
    };

    const handleInviteUser = async (userData: {
        firstName: string;
        lastName: string;
        email: string;
        role: string;
    }) => {
        try {
            setIsInviting(true);
            setError(null);

            await apiClient.inviteUser(userData);

            toast.success(`Invitation sent successfully to ${userData.email}!`);

            setShowInviteModal(false);
            await loadUsers();
        } catch (err: any) {
            setError(err.message || 'Failed to send invitation');
            throw err;
        } finally {
            setIsInviting(false);
        }
    };

    const handleEditUser = async (userData: {
        firstName: string;
        lastName: string;
        role: string;
    }) => {
        if (!selectedUser) return;

        try {
            setIsUpdating(true);
            setError(null);

            await apiClient.updateUser(selectedUser.id, {
                ...userData,
                email: selectedUser.email, // Email is required by API but not editable
            });

            setShowEditModal(false);
            setSelectedUser(null);
            await loadUsers();
        } catch (err: any) {
            setError(err.message || 'Failed to update user');
            throw err;
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDeleteUser = async () => {
        if (!userToDelete) return;

        try {
            setIsDeleting(true);
            setError(null);

            await apiClient.deleteUser(userToDelete.id);

            setShowDeleteModal(false);
            setUserToDelete(null);
            await loadUsers();
        } catch (err: any) {
            setError(err.message || 'Failed to deactivate user');
            throw err;
        } finally {
            setIsDeleting(false);
        }
    };

    const handleReactivateUser = async () => {
        if (!userToReactivate) return;

        try {
            setIsReactivating(true);
            setError(null);

            await apiClient.reactivateUser(userToReactivate.id);

            setShowReactivateModal(false);
            setUserToReactivate(null);
            await loadUsers();
        } catch (err: any) {
            setError(err.message || 'Failed to reactivate user');
            throw err;
        } finally {
            setIsReactivating(false);
        }
    };

    const handleResendInvitation = async (user: User) => {
        try {
            setError(null);
            await apiClient.resendInvitation(user.id);
            toast.success(`Invitation resent to ${user.email}!`);
            await loadUsers();
        } catch (err: any) {
            setError(err.message || 'Failed to resend invitation');
            toast.error(err.message || 'Failed to resend invitation');
        }
    };

    const totalPages = Math.ceil(totalCount / pageSize);

    return (
        <div className="max-w-7xl mx-auto px-4 py-8">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-start justify-between mb-2">
                    <div className="w-1/2 sm:flex-1">
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">User Management</h1>
                    </div>
                    <div className="flex space-x-2">
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="p-2 sm:inline-flex sm:items-center sm:px-4 sm:py-2 text-white text-sm font-medium rounded-md hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2 flex-shrink-0"
                            style={{ backgroundColor: '#69BE28', '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                            title="Create User"
                        >
                            <Plus className="w-4 h-4 sm:mr-2" />
                            <span className="hidden sm:inline">Create User</span>
                        </button>
                        <button
                            onClick={() => setShowInviteModal(true)}
                            className="p-2 sm:inline-flex sm:items-center sm:px-4 sm:py-2 text-gray-600 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 flex-shrink-0"
                            title="Invite User"
                        >
                            <Mail className="w-4 h-4 sm:mr-2" />
                            <span className="hidden sm:inline">Invite User</span>
                        </button>
                    </div>
                </div>
                <p className="text-sm sm:text-base text-gray-600 w-3/4 sm:w-full">
                    Manage team members and their permissions
                </p>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                        <Users className="w-8 h-8" style={{ color: '#69BE28' }} />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Total Users</p>
                            <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                        <UserCheck className="w-8 h-8 text-green-600" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Active Users</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {categoryCount.activeUsers}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                        <Clock className="w-8 h-8 text-yellow-600" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Pending Invites</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {categoryCount.pendingInvites}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                        <AlertTriangle className="w-8 h-8 text-orange-600" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Expired Invites</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {categoryCount.expiredInvites}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="mb-6 bg-white p-4 rounded-lg shadow">
                <div className="flex flex-col sm:flex-row gap-4">
                    {/* Search */}
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by name or email..."
                            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2"
                            style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                        />
                    </div>

                    {/* Role Filter */}
                    <div className="sm:w-48">
                        <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2"
                            style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                            value={roleFilter}
                            onChange={(e) => handleRoleFilter(e.target.value)}
                        >
                            <option value="all">All Roles</option>
                            <option value="admin">Admin</option>
                            <option value="manager">Manager</option>
                            <option value="compliance">Compliance</option>
                            <option value="user">User</option>
                        </select>
                    </div>

                    {/* Status Filter */}
                    <div className="sm:w-48">
                        <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2"
                            style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                            value={statusFilter}
                            onChange={(e) => handleStatusFilter(e.target.value)}
                        >
                            <option value="active">Active Users</option>
                            <option value="all">All Users</option>
                            <option value="inactive">Inactive Users</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Results Summary */}
            <div className="mb-4 flex justify-between items-center">
                <p className="text-sm text-gray-600">
                    Showing {users.length} of {totalCount} users
                </p>
                {searchTerm && (
                    <button
                        onClick={() => handleSearch('')}
                        className="text-sm hover:opacity-80 transition-opacity"
                        style={{ color: '#69BE28' }}
                    >
                        Clear search
                    </button>
                )}
            </div>

            {/* Error Display */}
            {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                    <div className="flex items-center">
                        <AlertTriangle className="w-4 h-4 text-red-500 mr-2" />
                        <span className="text-red-800">{error}</span>
                    </div>
                </div>
            )}

            {/* Users Table */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
                {isLoading ? (
                    <div className="p-8 text-center">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: '#69BE28' }} />
                        <p className="mt-2 text-gray-600">Loading users...</p>
                    </div>
                ) : users.length === 0 ? (
                    <div className="p-8 text-center">
                        <Users className="w-12 h-12 mx-auto text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            {searchTerm ? 'Try adjusting your search criteria.' : 'Get started by adding your first user.'}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        User
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Role
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Last Login
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Created
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {users.map((userData) => {
                                    const status = getUserStatus(userData);
                                    const StatusIcon = status.icon;
                                    
                                    return (
                                        <tr key={userData.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {userData.firstName} {userData.lastName}
                                                    </div>
                                                    <div className="text-sm text-gray-500">{userData.email}</div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeClass(userData.role)}`}>
                                                    {formatRoleName(userData.role)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.bgColor} ${status.color}`}>
                                                    <StatusIcon className="w-3 h-3 mr-1" />
                                                    {status.label}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {formatDate(userData.lastLogin)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {formatDate(userData.createdAt)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <div className="flex space-x-2">
                                                    {/* View - Always available */}
                                                    <button
                                                        onClick={() => {
                                                            setUserToView(userData);
                                                            setShowViewModal(true);
                                                        }}
                                                        className="hover:opacity-80 transition-opacity"
                                                        style={{ color: '#69BE28' }}
                                                        title="View User Details"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                    
                                                    {/* Edit - Available for own account or if admin */}
                                                    <button
                                                        onClick={() => {
                                                            setSelectedUser(userData);
                                                            setShowEditModal(true);
                                                        }}
                                                        className="hover:opacity-80 transition-opacity"
                                                        style={{ color: '#69BE28' }}
                                                        title="Edit User"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </button>
                                                    
                                                    {/* Deactivate/Reactivate/Resend - Only if NOT own account */}
                                                    {userData.id !== user.id && (
                                                        isPendingInvitation(userData) ? (
                                                            // Pending invitation: show nothing (only View and Edit)
                                                            null
                                                        ) : isExpiredInvitation(userData) ? (
                                                            // Expired invitation: show Resend button
                                                            <button
                                                                onClick={() => handleResendInvitation(userData)}
                                                                className="hover:opacity-80 transition-opacity"
                                                                style={{ color: '#69BE28' }}
                                                                title="Resend Invitation"
                                                            >
                                                                <Mail className="w-4 h-4" />
                                                            </button>
                                                        ) : userData.isActive ? (
                                                            // Active user: show Deactivate button
                                                            <button
                                                                onClick={() => {
                                                                    setUserToDelete(userData);
                                                                    setShowDeleteModal(true);
                                                                }}
                                                                className="text-red-600 hover:text-red-900"
                                                                title="Deactivate User"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        ) : (
                                                            // Inactive user with no invitation: show Reactivate button
                                                            <button
                                                                onClick={() => {
                                                                    setUserToReactivate(userData);
                                                                    setShowReactivateModal(true);
                                                                }}
                                                                className="text-green-600 hover:text-green-900"
                                                                title="Reactivate User"
                                                            >
                                                                <CheckCircle className="w-4 h-4" />
                                                            </button>
                                                        )
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="mt-6 flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                        Page {currentPage} of {totalPages}
                    </div>
                    <div className="flex space-x-2">
                        <button
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>
                        <button
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            {/* Create User Modal */}
            <CreateUserModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSubmit={handleCreateUser}
                isCreating={isCreating}
            />

            {/* Invite User Modal */}
            <InviteUserModal
                isOpen={showInviteModal}
                onClose={() => setShowInviteModal(false)}
                onSubmit={handleInviteUser}
                isInviting={isInviting}
            />

            {/* Temporary Password Modal */}
            {createdUserData && (
                <TemporaryPasswordModal
                    isOpen={showPasswordModal}
                    onClose={() => {
                        setShowPasswordModal(false);
                        setCreatedUserData(null);
                    }}
                    userData={{
                        firstName: createdUserData.firstName,
                        lastName: createdUserData.lastName,
                        email: createdUserData.email,
                    }}
                    temporaryPassword={createdUserData.temporaryPassword}
                />
            )}

            {/* Edit User Modal */}
            <EditUserModal
                isOpen={showEditModal}
                onClose={() => {
                    setShowEditModal(false);
                    setSelectedUser(null);
                }}
                onSubmit={handleEditUser}
                isUpdating={isUpdating}
                user={selectedUser}
                currentUserId={user.id}
            />

            {/* Delete Confirmation Modal */}
            <ConfirmDeleteModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setUserToDelete(null);
                }}
                onConfirm={handleDeleteUser}
                isDeleting={isDeleting}
                user={userToDelete ? {
                    firstName: userToDelete.firstName,
                    lastName: userToDelete.lastName,
                    email: userToDelete.email
                } : null}
            />

            {/* Reactivate Confirmation Modal */}
            <ConfirmReactivateModal
                isOpen={showReactivateModal}
                onClose={() => {
                    setShowReactivateModal(false);
                    setUserToReactivate(null);
                }}
                onConfirm={handleReactivateUser}
                isReactivating={isReactivating}
                user={userToReactivate ? {
                    firstName: userToReactivate.firstName,
                    lastName: userToReactivate.lastName,
                    email: userToReactivate.email,
                    role: userToReactivate.role
                } : null}
            />

            {/* View User Details Modal */}
            <UserViewModal
                isOpen={showViewModal}
                onClose={() => {
                    setShowViewModal(false);
                    setUserToView(null);
                }}
                user={userToView}
                currentUserId={user.id}
                onEdit={() => {
                    setSelectedUser(userToView);
                    setShowEditModal(true);
                }}
                onDeactivate={() => {
                    setUserToDelete(userToView);
                    setShowDeleteModal(true);
                }}
                onReactivate={() => {
                    setUserToReactivate(userToView);
                    setShowReactivateModal(true);
                }}
                onRefresh={loadUsers}
            />
        </div>
    );
};

export default UserManagement;